import {
  Box,
  Button,
  Grid,
  LinearProgress,
  Link as MuiLink,
  Step,
  StepContent,
  StepLabel,
  Stepper,
  styled
} from "@mui/material";
import MxTypography from "@rf-l1/MxTypography";
import MxButton2 from "@rf-l1/buttons/MxButton2";
import MxModalTemplate2 from "@rf-l1/modal/MxModalTemplate2";
import useModal from "@rf/base/modal/useModal";
import { memo, useCallback, useMemo, useState } from "react";
import type { KeyboardEvent, MouseEvent } from "react";
import MxFileDropzone from "@rf-l1/file/MxFileDraggable";
import { IFileData } from "@rf-l1/file/helpers/create-file-data";
import { useMxFileDraggable } from "@rf-l1/file/hooks/useMxFileDraggable";
import readXlsxFile from "read-excel-file";
import clearExcelData from "./useUpTemplateBuilding/helps/clearExcelData";
import { determinePropertyIdentifier } from "./useUpTemplateBuilding/helps/identifierTypeTemplate";
import {
  coverApartmentTemplate,
  coverComercialSiteTemplate,
  coverHouseTemplate,
  coverIndustrialTemplate,
  coverLandTemplate
} from "./useUpTemplateBuilding/helps/converTemplates";
import { processAllSets } from "./useUpTemplateBuilding/helps/getBuildingGroups";
import {
  ETypeTemplate,
  IBuildingFromTemplateAdapter
} from "./useUpTemplateBuilding/models/upTemplate.model";
import { I_GENERIC_ITEMS } from "@/src-refactor/modules/shared/model/interface/items.interface";
import {
  ExcelFilesPath,
  getExcelFilesPath
} from "@/src-refactor/modules/shared/model/const/excel-files-path.const";
import toast from "react-hot-toast";
import { IconFileTypeXls, IconUpload } from "@tabler/icons-react";
import { border, borderRadius, fontWeight } from "@mui/system";
type UseImportBuildingsUnitsModalProps = {
  typeProperties: I_GENERIC_ITEMS[];
};

/**
 * Modal de importación de etapas y unidades, basado en useModal
 * Retorna el evento del modal; si `type.confirm`, el `value` es el payload adaptado
 */
const useImportBuildingsUnitsModal = ({
  typeProperties
}: UseImportBuildingsUnitsModalProps) => {
  const { modal } = useModal();

  const handleOpen = () => {
    return modal({
      template: <MxModalTemplate2 children={<Template />} />,
      data: { typeProperties },
      options: {
        disableEscapeKeyDown: true,
        disableBackdropClick: true,
        title: "Importar etapas y unidades",
        size: {
          xs: { width: "100%", height: "100%", maxHeight: 640 },
          sm: { width: "100%", height: "100%", maxHeight: 640 },
          md: { width: 500 }
        }
      }
    });
  };

  return { handleOpen };
};

export default useImportBuildingsUnitsModal;

// -------------- Template --------------
const formatFileSize = (size: number) => {
  if (!size) {
    return "0 KB";
  }
  if (size < 1024) {
    return `${size} B`;
  }
  if (size < 1024 * 1024) {
    return `${Math.round(size / 1024)} KB`;
  }
  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
};

const Template = () => {
  const { getData, onConfirm, onClose } = useModal();
  const { typeProperties } = getData() as { typeProperties: I_GENERIC_ITEMS[] };

  const [files, setFiles] = useState<IFileData[]>([]);
  const [isProcessing, setProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [summary, setSummary] = useState<{
    buildings: number;
    units: number;
  } | null>(null);
  const [payload, setPayload] = useState<IBuildingFromTemplateAdapter[] | null>(
    null
  );

  const allowedTypes = useMemo(() => [".xlsx" as const], []);
  const downloadTemplate = useCallback(() => {
    if (!typeProperties || typeProperties.length === 0) {
      toast.error("Se requiere seleccionar tipo de proyecto");
      return;
    }
    const idType = typeProperties[0].id as unknown as ExcelFilesPath;
    const info = getExcelFilesPath(idType);
    if (!info) {
      toast.error("No hay plantilla disponible para este tipo");
      return;
    }
    const link = document.createElement("a");
    link.href = info.path;
    link.download = info.name;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }, [JSON.stringify(typeProperties)]);
  const INVALID_TEMPLATE_TOAST_ID = "invalid-template";
  const INVALID_TEMPLATE_MESSAGE =
    "La plantilla no es válida. Verifica las columnas correspondientes.";

  const handleProcess = useCallback(async (file: File) => {
    try {
      setProcessing(true);
      setSummary(null);
      setPayload(null);
      setProgress(10);

      // Simular progreso mientras se procesa
      const timer = setInterval(() => {
        setProgress(prev => (prev < 90 ? prev + 10 : prev));
      }, 200);

      const dataRowsDirty = await readXlsxFile(file, {});
      const headerRowIndex = dataRowsDirty.findIndex(
        row => row.includes("Nombre de conjunto") && row.includes("Fecha de entrega")
      );
      const relevantData = dataRowsDirty.slice(headerRowIndex);
      const { headerRow, dataRows, cleanData } = clearExcelData(relevantData);
      const type = determinePropertyIdentifier(headerRow);

      if (type === "invalid") {
        toast.error(INVALID_TEMPLATE_MESSAGE, {
          id: INVALID_TEMPLATE_TOAST_ID
        });
        throw new Error("Invalid template");
      }

      const buildings = processAllSets(cleanData);
      let adapted: IBuildingFromTemplateAdapter[] = [];
      if (type === ETypeTemplate.land) {
        adapted = coverLandTemplate(buildings);
      } else if (type === ETypeTemplate.industrialSite) {
        adapted = coverIndustrialTemplate(buildings);
      } else if (type === ETypeTemplate.house) {
        adapted = coverHouseTemplate(buildings);
      } else if (type === ETypeTemplate.comercialLocals) {
        adapted = coverComercialSiteTemplate(buildings);
      } else if (type === ETypeTemplate.apartment) {
        adapted = coverApartmentTemplate(buildings);
      }

      const totalBuildings = adapted.length;
      const totalUnits = adapted.reduce((acc, b) => acc + (b.units?.length || 0), 0);

      setPayload(adapted);
      setSummary({ buildings: totalBuildings, units: totalUnits });
      setProgress(100);
      clearInterval(timer);
    } catch (e) {
      setProgress(0);
      setSummary(null);
      setPayload(null);
      setFiles([]);
      console.error(e);
    } finally {
      setProcessing(false);
    }
  }, []);
  const onChangeFiles = useCallback(
    (e: any) => {
      const next = e.target.value as IFileData[];
      setFiles(next);
      const fileObj = next?.[0]?.file as File | undefined;
      if (fileObj) {
        handleProcess(fileObj);
      }
    },
    [handleProcess]
  );
  const activeStep = useMemo(() => {
    if (payload) {
      return 2;
    }
    if (files.length > 0 || isProcessing) {
      return 1;
    }
    return 0;
  }, [files.length, isProcessing, payload]);

  const steps = useMemo(
    () => [
      {
        label: "Formato de plantilla",
        content: <FormatStepContent onDownload={downloadTemplate} />
      },
      {
        label: "Importar plantilla",
        content: (
          <ImportStepContent
            allowedTypes={allowedTypes as unknown as any}
            files={files}
            isProcessing={isProcessing}
            onChangeFiles={onChangeFiles}
            progress={progress}
          />
        )
      },
      {
        label: "Confirmación",
        content: <ConfirmationStepContent summary={summary} />
      }
    ],
    [
      allowedTypes,
      downloadTemplate,
      files,
      isProcessing,
      onChangeFiles,
      progress,
      summary
    ]
  );

  const handleConfirm = () => {
    if (payload) {
      onConfirm(payload);
    }
  };

  return (
    <Grid container rowGap={2}>
      <Grid item xs={12}>
        <CustomStep activeStep={undefined} orientation="vertical">
          {steps.map((step, index) => (
            <Step key={step.label} completed={false} expanded>
              <StepLabel>{step.label}</StepLabel>
              <StepContent>{step.content}</StepContent>
            </Step>
          ))}
        </CustomStep>
      </Grid>

      {/* Acciones */}
      <Grid container item xs={12} columnSpacing={1}>
        <Grid item flex={1} />
        <Grid item>
          <MxButton2 variant="text" textColor="error.main" onClick={onClose}>
            Cancelar
          </MxButton2>
        </Grid>
        <Grid item>
          <MxButton2
            variant="contained"
            bgColor="primary.main"
            disabled={!payload}
            onClick={handleConfirm}
          >
            Importar
          </MxButton2>
        </Grid>
      </Grid>
    </Grid>
  );
};

type FormatStepContentProps = {
  onDownload: () => void;
};

const FormatStepContent = memo(({ onDownload }: FormatStepContentProps) => (
  <Grid container alignItems="flex-start" rowGap={1}>
    <Grid item xs={12}>
      <MxTypography variant="overline" color="grey.600">
        Completa la información de las etapas y unidades. <br />
        <Box component="span" color="error.main" mt={0.4}>
          (Importante: no modifiques la estructura del archivo)
        </Box>
      </MxTypography>
    </Grid>
    <Grid item xs={12}>
      <Button variant="outlined" size="small" onClick={onDownload}>
        Descargar plantilla
      </Button>
    </Grid>
  </Grid>
));

FormatStepContent.displayName = "FormatStepContent";

type ImportStepContentProps = {
  allowedTypes: any;
  files: IFileData[];
  isProcessing: boolean;
  onChangeFiles: (event: any) => void;
  progress: number;
};

const ImportStepContent = memo(
  ({
    allowedTypes,
    files,
    isProcessing,
    onChangeFiles,
    progress
  }: ImportStepContentProps) => {
    const progressValue = Math.min(Math.round(progress), 100);
    const isComplete = progressValue >= 100;

    return (
      <Grid container gap={1} alignItems="flex-start">
        <Grid item xs={12}>
          <MxFileDropzone
            name="import-buildings"
            values={files}
            onChange={onChangeFiles}
            onBlur={() => {}}
            multiple={false}
            maxSize={20}
            minSize={1}
            allowedTypes={allowedTypes}
          >
            <PlaceHolderDraft />
          </MxFileDropzone>
        </Grid>
        {files.length > 0 && (
          <Grid item xs={12}>
            <ItemFile>
              <Box className="item-file__icon">
                <IconFileTypeXls size={28} strokeWidth={1.5} />
              </Box>
              <Box className="item-file__info">
                <MxTypography
                  variant="overline"
                  className="item-file__title"
                  lines={1}
                  noWrap
                >
                  {files[0]?.name ?? "Plantilla de propiedades"}
                </MxTypography>
                <MxTypography variant="overline" className="item-file__meta">
                  {formatFileSize(files[0]?.size ?? 0)}
                  {isProcessing ? " | Procesando archivo" : ""}
                </MxTypography>
                {(isProcessing || progress > 0) && (
                  <LinearProgress
                    variant="determinate"
                    value={Math.min(progress, 100)}
                    className="item-file__progress"
                  />
                )}
              </Box>
              {(isProcessing || progress > 0) && (
                <MxTypography
                  variant="overline"
                  className={`item-file__percentage${
                    isComplete ? " item-file__percentage--completed" : ""
                  }`}
                >
                  {isComplete ? "Completado" : `${progressValue}%`}
                </MxTypography>
              )}
            </ItemFile>
          </Grid>
        )}
      </Grid>
    );
  }
);

ImportStepContent.displayName = "ImportStepContent";

type ConfirmationStepContentProps = {
  summary: {
    buildings: number;
    units: number;
  } | null;
};

const ConfirmationStepContent = memo(({ summary }: ConfirmationStepContentProps) => (
  <Grid container alignItems="flex-start">
    <MxTypography variant="overline" color="grey.600">
      Asegúrate de haber revisado la información con detenimiento.
    </MxTypography>
    {summary && (
      <Grid container mt={1}>
        <Grid item xs={12}>
          <MxTypography variant="overline" color="grey.700">
            Cantidad de etapas incluidas en el Excel: {summary.buildings}
          </MxTypography>
        </Grid>
        <Grid item xs={12}>
          <MxTypography variant="overline" color="grey.700">
            Cantidad de unidades incluidas en el Excel: {summary.units}
          </MxTypography>
        </Grid>
      </Grid>
    )}
  </Grid>
));

ConfirmationStepContent.displayName = "ConfirmationStepContent";

const StyledPlaceHolderDraft = styled(Box)(({ theme }) => ({
  width: "100%",
  border: "1px solid",
  borderColor: theme.palette.grey[300],
  borderRadius: 10,
  padding: 30,
  cursor: "pointer",
  bgcolor: "grey.50",
  margin: "1rem 0rem",
  display: "flex",
  justifyContent: "center",
  flexDirection: "column",
  alignItems: "center",
  rowGap: 6,
  svg: {
    color: theme.palette.primary.main
  }
}));

const PlaceHolderDraft = memo(() => {
  const { openFileExplore } = useMxFileDraggable();

  const handleClick = useCallback(
    (event: MouseEvent<HTMLDivElement>) => {
      event.stopPropagation();
      openFileExplore(event);
    },
    [openFileExplore]
  );

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        event.stopPropagation();
        openFileExplore();
      }
    },
    [openFileExplore]
  );

  const handleLinkClick = useCallback(
    (event: MouseEvent<HTMLElement>) => {
      event.preventDefault();
      event.stopPropagation();
      openFileExplore(event as unknown as MouseEvent<HTMLDivElement>);
    },
    [openFileExplore]
  );

  return (
    <StyledPlaceHolderDraft
      role="button"
      tabIndex={0}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
    >
      <IconUpload />
      <Box display="flex" columnGap={0.5} alignItems="center">
        <MxTypography variant="overline" color="grey.600" textAlign={"center"}>
          Arrastrar y soltar o
        </MxTypography>
        <MuiLink
          fontSize={12.3}
          component="button"
          underline="always"
          mr={2}
          onClick={handleLinkClick}
        >
          seleccionar
        </MuiLink>
        <MxTypography variant="overline" color="grey.600" textAlign={"center"}>
          el documento a subir.
        </MxTypography>
      </Box>
    </StyledPlaceHolderDraft>
  );
});

PlaceHolderDraft.displayName = "PlaceHolderDraft";

const ItemFile = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1.5),
  width: "100%",
  padding: theme.spacing(1.5, 1),
  borderRadius: 12,
  border: `1px solid ${theme.palette.grey[200]}`,
  backgroundColor: theme.palette.grey[50],
  "& .item-file__icon": {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: 40,
    height: 40,
    borderRadius: 8,
    backgroundColor: theme.palette.common.white,
    border: `1px solid ${theme.palette.grey[200]}`
  },
  "& .item-file__icon svg": {
    color: theme.palette.primary.main
  },
  "& .item-file__info": {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(0.3),
    flex: 1,
    minWidth: 0
  },
  "& .item-file__title": {
    color: theme.palette.grey[800],
    fontWeight: 600,
    fontSize: 14
  },
  "& .item-file__meta": {
    display: "flex",
    gap: theme.spacing(0.5),
    alignItems: "center",
    color: theme.palette.grey[600]
  },
  "& .item-file__progress": {
    marginTop: theme.spacing(1)
  },
  "& .item-file__percentage": {
    color: theme.palette.grey[700],
    fontWeight: 600,
    fontSize: 12,
    minWidth: 48,
    textAlign: "right"
  },
  "& .item-file__percentage--completed": {
    color: theme.palette.success.main
  },
  ".MuiLinearProgress-root": {
    height: 6,
    borderRadius: 999,
    backgroundColor: theme.palette.grey[200]
  },
  ".MuiLinearProgress-bar": {
    borderRadius: 999,
    backgroundColor: theme.palette.primary.main
  }
}));

const CustomStep = styled(Stepper)(({ theme }) => ({
  ".MuiStepLabel-label": {
    color: "#595A59",
    fontWeight: 500
  },
  ".MuiStepLabel-iconContainer": {
    svg: {
      border: "1px solid #aaa",
      borderRadius: 50,
      fill: "transparent",
      text: {
        fill: theme.palette.primary.main
      }
    }
  }
}));
